import {Button, DataStatus, Flag, Separator, showNotification} from 'platform/components';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import type {ReactNode} from 'react';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  type ConfigurationCustomerViewResponseBody,
  useExportConfigurationOfferSheetMutation,
  useGetConfigurationCustomerViewQuery,
} from '@dms/api/new-vehicle';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {parseDate, type ApiError} from 'shared/web';

import {MoneyText} from '../MoneyText/MoneyText';

export type ConfigurationCustomerViewProps = {configurationId: string};
type CustomerViewData = ConfigurationCustomerViewResponseBody;
type CustomerViewLine = CustomerViewData['lines'][number];

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});
const lineTypeLabel = (type: string) =>
  match(type)
    .with('vehicle', () => t('entity.vehicle.labels.vehicle', 'Vehicle'))
    .with('engine', () => t('entity.vehicle.labels.engine', 'Engine'))
    .with('colour', () => t('entity.newVehicle.labels.colour', 'Colour'))
    .with('upholstery', () => t('entity.newVehicle.labels.upholstery', 'Upholstery'))
    .with('feature', () => t('entity.vehicle.labels.features', 'Features'))
    .with('package', () => t('entity.workshopServicePackages.labels.packages', 'Packages'))
    .otherwise(() => t('general.labels.variant', 'Variant'));

const groupLines = (lines: CustomerViewLine[]) =>
  lines.reduce<Map<string, CustomerViewLine[]>>((groups, line) => {
    groups.set(line.type, [...(groups.get(line.type) ?? []), line]);
    return groups;
  }, new Map());

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const openPdf = (pdf: Blob, target: Window | null) => {
  if (!target) {
    downloadBlob(pdf, 'offer-sheet.pdf');
    return;
  }

  target.location.assign(URL.createObjectURL(pdf));
};

const isApiError = (error: unknown): error is ApiError =>
  typeof error === 'object' && error !== null;

const printHtml = (html: string, target: Window | null) => {
  const htmlBlob = new Blob([html], {type: 'text/html'});
  const printWindow = target ?? window.open('', '_blank');
  if (!printWindow) {
    downloadBlob(htmlBlob, 'offer-sheet.html');
    return;
  }
  const url = URL.createObjectURL(htmlBlob);
  printWindow.opener = null;
  printWindow.addEventListener(
    'load',
    () => {
      printWindow.focus();
      printWindow.print();
      URL.revokeObjectURL(url);
    },
    {once: true}
  );
  printWindow.location.assign(url);
};

function DiscountContent(props: {discount: CustomerViewData['discount']; currency: string}) {
  if (!props.discount) {
    return null;
  }

  const basis =
    props.discount.basis === 'gross'
      ? t('entity.newVehicle.labels.grossBasis', 'gross basis')
      : t('entity.newVehicle.labels.netBasis', 'net basis');

  return (
    <HStack spacing={2} align="baseline">
      {props.discount.kind === 'percent' ? (
        <Text size="small" align="right" color="success" alternative>
          {`−${props.discount.value} %`}
        </Text>
      ) : (
        <MoneyText
          amount={props.discount.value}
          currency={props.currency}
          size="small"
          align="right"
          color="success"
          alternative
          noWrap
        />
      )}
      <Text size="xSmall" color="tertiary">
        {basis}
      </Text>
    </HStack>
  );
}

function TotalRow(props: {
  label: string;
  currency: string;
  amount?: string | null;
  content?: ReactNode;
  isEmphasised?: boolean;
}) {
  return (
    <HStack justify="space-between" align="center" spacing={4}>
      <Text size="small" color="secondary" alternative>
        {props.label}
      </Text>
      <Show when={props.content}>{props.content}</Show>
      <Show when={isNotNil(props.amount) && !props.content}>
        <MoneyText
          amount={props.amount ?? ''}
          currency={props.currency}
          size="small"
          color={props.isEmphasised ? 'link' : 'primary'}
          alternative={props.isEmphasised}
          align="right"
          noWrap
        />
      </Show>
    </HStack>
  );
}

export function ConfigurationCustomerView(props: ConfigurationCustomerViewProps) {
  const query = useGetConfigurationCustomerViewQuery({configurationId: props.configurationId});
  const [exportOfferSheet, exportState] = useExportConfigurationOfferSheetMutation();
  const data = query.currentData;
  const formatDate = useDateTimeFormatter();
  const currency = data?.currency ?? '';
  const printOfferSheet = () => {
    const target = window.open('', '_blank');
    if (target) {
      target.opener = null;
    }

    void exportOfferSheet({
      configurationId: props.configurationId,
      accept: 'application/pdf',
      exportConfigurationOfferSheetRequestBody: {},
    })
      .unwrap()
      .catch(() =>
        exportOfferSheet({
          configurationId: props.configurationId,
          accept: 'text/html',
          exportConfigurationOfferSheetRequestBody: {},
        }).unwrap()
      )
      .then((offerSheet) => {
        if (typeof offerSheet !== 'string') {
          openPdf(offerSheet, target);
          return;
        }
        printHtml(offerSheet, target);
        showNotification.info(
          t(
            'entity.newVehicle.notifications.offerSheetHtmlFallback',
            'The offer sheet opened in a printable format.'
          )
        );
      })
      .catch((error: unknown) => {
        target?.close();
        if (isApiError(error)) {
          handleApiError(error);
        } else {
          showNotification.error(t('general.labels.somethingWentWrong', 'Something went wrong.'));
        }
      });
  };

  return (
    <DataStatus
      isLoading={query.isLoading || query.isFetching}
      isError={query.isError}
      errorAction={{
        title: t('general.actions.tryAgain', 'Try again'),
        onClick: () => void query.refetch(),
        variant: 'secondary',
      }}
      isEmpty={data?.lines.length === 0}
      emptyMessage={t('entity.newVehicle.customerView.empty', 'No customer view is available')}
    >
      <HStack justify="center" width="100%">
        <Box width="100%" maxWidth={220} paddingHorizontal={[2, 4, 6, 6]}>
          <VStack spacing={[4, 5, 6, 6]} data-testid="new-vehicle-customer-view">
            <VStack spacing={3}>
              <HStack justify="space-between" align="flex-start" spacing={4} wrap>
                <VStack spacing={1}>
                  <Text size="xSmall" color="secondary" alternative>
                    {t('entity.newVehicle.customerView.offerFor', 'Offer for').toUpperCase()}
                  </Text>
                  <Heading size={2}>{data?.label}</Heading>
                </VStack>
                <Show when={data?.validityDate}>
                  <Flag
                    size="small"
                    colorScheme="blue"
                    isSubtle
                    label={`${t('general.labels.validUntil', 'Valid until')} ${
                      data?.validityDate
                        ? formatDate('dateMedium', parseDate(data.validityDate))
                        : ''
                    }`}
                  />
                </Show>
              </HStack>
              <Box
                backgroundColor="palettes.blue.10.100"
                border="1px solid"
                borderColor="palettes.blue.30.100"
                borderRadius="large"
                paddingHorizontal={[4, 5, 6, 6]}
                paddingVertical={4}
              >
                <HStack justify="space-between" align="flex-end" spacing={4} wrap>
                  <VStack spacing={0}>
                    <Text size="small" color="secondary" alternative>
                      {t('entity.newVehicle.labels.grossToCustomer', 'Gross to customer')}
                    </Text>
                    <Text size="xSmall" color="tertiary">
                      {t('entity.newVehicle.labels.inclVat', 'incl. VAT')}
                    </Text>
                  </VStack>
                  <MoneyText
                    amount={data?.totalGross ?? ''}
                    currency={currency}
                    headingSize={1}
                    color="link"
                    align="right"
                    noWrap
                  />
                </HStack>
              </Box>
            </VStack>

            <VStack spacing={5}>
              {[...groupLines(data?.lines ?? [])].map(([type, lines]) => (
                <VStack key={type} spacing={2}>
                  <Heading size={5}>{lineTypeLabel(type)}</Heading>
                  <VStack spacing={2}>
                    {lines.map((line) => (
                      <HStack
                        key={`${line.type}-${line.id}`}
                        justify="space-between"
                        align="flex-start"
                        spacing={4}
                      >
                        <Text size="small">{line.label}</Text>
                        <MoneyText
                          amount={line.unitGross}
                          currency={currency}
                          size="small"
                          align="right"
                          alternative
                          noWrap
                        />
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
              ))}
            </VStack>

            <Separator spacing={0} />
            <VStack spacing={3}>
              <Heading size={5}>{t('entity.newVehicle.customerView.totals', 'Totals')}</Heading>
              <TotalRow
                label={t('entity.newVehicle.customerView.listGross', 'List price (gross)')}
                amount={data?.listTotalGross}
                currency={currency}
              />
              <Show when={data?.discount}>
                <TotalRow
                  label={t('entity.newVehicle.customerView.discount', 'Proposed discount')}
                  currency={currency}
                  content={
                    <DiscountContent discount={data?.discount ?? null} currency={currency} />
                  }
                />
              </Show>
              <TotalRow
                label={t('entity.newVehicle.labels.ofWhichVat', 'Of which VAT')}
                amount={data?.vat}
                currency={currency}
              />
              <TotalRow
                label={t('entity.newVehicle.customerView.totalGross', 'Total gross')}
                amount={data?.totalGross}
                currency={currency}
                isEmphasised
              />
              <Show when={isNotNil(data?.proposedSellingGross)}>
                <TotalRow
                  label={t(
                    'entity.newVehicle.labels.proposedSellingPriceGross',
                    'Proposed selling price (gross)'
                  )}
                  amount={data?.proposedSellingGross}
                  currency={currency}
                  isEmphasised
                />
              </Show>
            </VStack>

            <Show when={isNotNilOrEmpty(data?.codes)}>
              <Box backgroundColor="palettes.neutral.10.100" borderRadius="medium" padding={4}>
                <VStack spacing={2}>
                  <Heading size={5}>
                    {t('entity.newVehicle.customerView.codes', 'Factory codes')}
                  </Heading>
                  {data?.codes.map((code) => (
                    <HStack key={`${code.label}-${code.code}`} justify="space-between" spacing={4}>
                      <Text size="xSmall" color="secondary">
                        {code.label}
                      </Text>
                      <Text size="xSmall" alternative>
                        {code.code}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </Show>
            <Show when={isNotNilOrEmpty(data?.disclaimer)}>
              <Text size="xSmall" color="tertiary">
                {data?.disclaimer}
              </Text>
            </Show>
            <HStack justify="flex-end">
              <Button
                title={t('entity.newVehicle.actions.printOfferSheet', 'Print offer sheet')}
                leftIcon="action/print"
                isLoading={exportState.isLoading}
                onClick={printOfferSheet}
              />
            </HStack>
          </VStack>
        </Box>
      </HStack>
    </DataStatus>
  );
}
